import { useEffect, useId, useMemo, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { Field, useFormikContext } from "formik";
import { useTranslation } from "react-i18next";
import Col from "../element/col";
import Container from "../element/container";
import Icon from "../element/icon";
import Input from "../element/input";
import Row from "../element/row";
import { DEFAULT_PHONE_COUNTRY, getPhoneCountries, getPhonePlaceholder } from "../../helpers/phone";

const MENU_WIDTH = 340;
const MENU_HEIGHT = 400;

// Where the desktop popover opens: under the code, or above it when the
// viewport has more room there. The phone layout is a bottom sheet in CSS.
function placeMenu(trigger) {
  const rect = trigger.getBoundingClientRect();
  const width = Math.min(MENU_WIDTH, window.innerWidth - 16);
  const left = Math.max(8, Math.min(rect.left, window.innerWidth - width - 8));
  const below = window.innerHeight - rect.bottom - 14;
  const above = rect.top - 14;

  if (below >= 260 || below >= above) {
    return {
      "--menu-left": `${left}px`,
      "--menu-width": `${width}px`,
      "--menu-top": `${rect.bottom + 6}px`,
      "--menu-bottom": "auto",
      "--menu-height": `${Math.min(MENU_HEIGHT, below)}px`,
    };
  }

  return {
    "--menu-left": `${left}px`,
    "--menu-width": `${width}px`,
    "--menu-top": "auto",
    "--menu-bottom": `${window.innerHeight - rect.top + 6}px`,
    "--menu-height": `${Math.min(MENU_HEIGHT, above)}px`,
  };
}

// Scrolls only the list. scrollIntoView would also scroll every ancestor,
// which shifts the page or modal sitting behind the menu.
function revealOption(list, selector, block) {
  const option = list?.querySelector(selector);

  if (!option) return;

  const top = option.offsetTop;

  if (block === "center") {
    list.scrollTop = top - (list.clientHeight - option.offsetHeight) / 2;
  } else if (top < list.scrollTop) {
    list.scrollTop = top;
  } else if (top + option.offsetHeight > list.scrollTop + list.clientHeight) {
    list.scrollTop = top + option.offsetHeight - list.clientHeight;
  }
}

function CountryMenu({ countries, selected, trigger, onSelect, onClose }) {
  const { t } = useTranslation();
  const menuRef = useRef(null);
  const listRef = useRef(null);
  const [query, setQuery] = useState("");
  const [active, setActive] = useState(-1);
  const [position, setPosition] = useState(() => placeMenu(trigger));

  const matches = useMemo(() => {
    const text = query.trim().toLowerCase().replace(/^\+/, "");

    if (!text) return countries;

    return countries.filter(
      (item) =>
        item.name.toLowerCase().includes(text) ||
        item.country.toLowerCase() === text ||
        item.callingCode.startsWith(text),
    );
  }, [countries, query]);

  useEffect(() => {
    revealOption(listRef.current, '[aria-selected="true"]', "center");
    // Keyboard focus has to be inside the menu for Escape and the arrows; on
    // a phone the search is not focused, so the menu itself takes it.
    if (!menuRef.current?.contains(document.activeElement)) {
      menuRef.current?.focus({ preventScroll: true });
    }
  }, []);

  useEffect(() => {
    const reposition = () => setPosition(placeMenu(trigger));
    const openedAt = trigger.getBoundingClientRect();
    // The popover is fixed to the viewport, so it closes once the page behind
    // it scrolls the field away. Scrolls that leave the field in place (the
    // list itself, a modal settling) are ignored.
    const closeOnOutsideScroll = () => {
      // The phone layout is a sheet over a backdrop; nothing behind it moves.
      if (window.innerWidth < 768) return;

      const now = trigger.getBoundingClientRect();

      if (
        Math.abs(now.top - openedAt.top) > 24 ||
        Math.abs(now.left - openedAt.left) > 24
      ) {
        onClose();
      }
    };

    window.addEventListener("resize", reposition);
    document.addEventListener("scroll", closeOnOutsideScroll, true);

    return () => {
      window.removeEventListener("resize", reposition);
      document.removeEventListener("scroll", closeOnOutsideScroll, true);
    };
  }, [onClose, trigger]);

  useEffect(() => {
    revealOption(listRef.current, ".is-active", "nearest");
  }, [active]);

  const handleKeyDown = (event) => {
    if (event.key === "Escape") {
      event.preventDefault();
      event.stopPropagation();
      onClose();
    } else if (event.key === "ArrowDown") {
      event.preventDefault();
      setActive((index) => Math.min(index + 1, matches.length - 1));
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      setActive((index) => Math.max(index - 1, 0));
    } else if (event.key === "Enter") {
      event.preventDefault();
      const choice = matches[active] || (matches.length === 1 && matches[0]);
      if (choice) onSelect(choice.country);
    }
  };

  return (
    <div className="phone-country-layer" onKeyDown={handleKeyDown}>
      <div className="phone-country-backdrop" onClick={onClose} />
      <div
        className="phone-country-menu"
        ref={menuRef}
        tabIndex={-1}
        role="dialog"
        aria-label={t("Select country code")}
        style={position}
      >
        <div className="phone-country-heading">
          <p>{t("Select country code")}</p>
          <button type="button" aria-label={t("Close")} onClick={onClose}>
            <Icon icon="majesticons:close-line" />
          </button>
        </div>
        <label className="phone-country-search">
          <Icon icon="solar:magnifer-linear" />
          <input
            type="text"
            // On a phone the keyboard would cover half the sheet straight away.
            autoFocus={window.innerWidth >= 768}
            value={query}
            placeholder={t("Search country or code")}
            aria-label={t("Search country or code")}
            onChange={(event) => {
              setQuery(event.target.value);
              setActive(0);
            }}
          />
        </label>
        <ul className="phone-country-list" role="listbox" ref={listRef}>
          {matches.map((item, index) => (
            <li
              key={item.country}
              role="option"
              aria-selected={item.country === selected}
              className={[
                index === active ? "is-active" : "",
                // Marks where the pinned countries end and the A-Z list starts.
                !query && item.pinned && !matches[index + 1]?.pinned
                  ? "is-last-pinned"
                  : "",
              ].join(" ")}
              onClick={() => onSelect(item.country)}
            >
              <span className="phone-country-flag" aria-hidden="true">
                {item.flag}
              </span>
              <span className="phone-country-name">{item.name}</span>
              <span className="phone-country-dial">+{item.callingCode}</span>
              {item.country === selected && (
                <Icon icon="solar:check-circle-bold" />
              )}
            </li>
          ))}
          {matches.length === 0 && (
            <li className="phone-country-empty">{t("No country found")}</li>
          )}
        </ul>
      </div>
    </div>
  );
}

// A phone field with a country code picker. The number is kept as typed in
// `name` and the ISO country in `countryName`; helpers/phone turns the pair
// into an E.164 number on submit.
export default function PhoneInput({
  name,
  countryName = `${name}_country`,
  label,
  placeholder,
  className,
  isRequired,
  invalid = true,
  variant,
}) {
  const fieldId = useId();
  const triggerRef = useRef(null);
  const [isOpen, setOpen] = useState(false);
  const { t, i18n } = useTranslation();
  const { errors, setFieldValue, values } = useFormikContext();
  const countries = useMemo(
    () => getPhoneCountries(i18n?.language || "en"),
    [i18n?.language],
  );
  const selected =
    countries.find((item) => item.country === values[countryName]) ||
    countries.find((item) => item.country === DEFAULT_PHONE_COUNTRY);

  const numberPlaceholder = placeholder ?? getPhonePlaceholder(selected.country);

  const close = () => {
    setOpen(false);
    triggerRef.current?.focus();
  };

  const picker = (
    <>
      <button
        type="button"
        ref={triggerRef}
        className={`phone-country ${isOpen ? "is-open" : ""} ${
          variant === "chip" ? "_label phone-code" : "prefix-frame"
        }`}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-label={`${t("Country code")}: ${selected.name} +${selected.callingCode}`}
        onClick={() => setOpen((open) => !open)}
      >
        <span className="phone-country-flag" aria-hidden="true">
          {selected.flag}
        </span>
        <span className="phone-country-code" aria-hidden="true">
          +{selected.callingCode}
        </span>
        <Icon icon="iconamoon:arrow-down-2" />
      </button>
      {isOpen &&
        // Inside a modal the menu joins that modal's layer, so it stacks above
        // it and stays scrollable under the modal's scroll lock.
        ReactDOM.createPortal(
          <CountryMenu
            countries={countries}
            selected={selected.country}
            trigger={triggerRef.current}
            onClose={close}
            onSelect={(country) => {
              setFieldValue(countryName, country);
              close();
            }}
          />,
          triggerRef.current?.closest(".full-modal") || document.body,
        )}
    </>
  );

  if (variant === "chip") {
    return (
      <Row className="_row phone-frame">
        {picker}
        <Input
          name={name}
          invalid={false}
          placeholder={numberPlaceholder}
          type="tel"
        />
      </Row>
    );
  }

  return (
    <Container className="w-100">
      <Col className={`element _input ${className || ""}`}>
        {label && (
          <Row>
            <label htmlFor={fieldId}>
              {label} {isRequired && <span>*</span>}
            </label>
          </Row>
        )}
        <Row>
          <article className="prefix-input phone-split">
            {picker}
            <Field
              id={fieldId}
              name={name}
              type="tel"
              inputMode="tel"
              autoComplete="tel-national"
              placeholder={numberPlaceholder}
            />
          </article>
        </Row>
      </Col>
      {invalid && errors?.[name] ? (
        <Row className="element _errors">{t(errors[name])}</Row>
      ) : null}
    </Container>
  );
}
